/**
 * The bar at the bottom of the declaration screen, and the sheet it opens.
 *
 * THERE IS NO SAVE BUTTON. The slate autosaves as it changes, so the left of the bar is a
 * statement rather than a control — the manager's work is safe and he does not have to do
 * anything about it. What the button on the right does is different in kind: it publishes. Until
 * he presses it his slate is his own business and the rules will not serve it to anybody else.
 *
 * The sheet is deliberately inert. Nothing in it can be changed, so nothing in it is drawn as
 * though it could — no chips that look tappable, no controls, no toggles. It answers one
 * question, "what does this cost me", and offers one way forward and one way back.
 */

import { useEffect, useRef } from 'react';
import { dock } from '../dock.js';
import { useScrollLock } from '../useScrollLock.js';
import { useDockedHeight } from '../useDockedHeight.js';
import { ClubChip, PositionChip } from '../chips.js';

/*
 * `dock` moved to `../dock.js` on 26 August, unchanged, because the bar was not the only thing
 * that needed it — the SUBMIT SHEET below is `position: fixed` too and never had it, so it was
 * landing at the bottom of a 2,837px `main` instead of the bottom of the screen. See that file.
 */


/*
 * Three, not 3. A confirmation is a sentence a person reads once, and small counts read as words
 * in one. Past five it is a figure again, because "eight" starts to feel like prose where the
 * number is the point.
 */
const WORD: Readonly<Record<number, string>> = {
  1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five',
};

export interface Line {
  player: string;
  club: string | null;
  pos?: string;
  /** `4.08`, or a round label before the lottery. */
  code: string;
  overall: number | null;
  tagged?: boolean;
}

export function CommitBar(
  { saved, disabled, onSubmit, submitted, onUpdate, onWithdraw, count, tax, picks }:
  {
    saved: 'saving' | 'saved' | 'offline';
    disabled?: boolean;
    onSubmit: () => void;
    /** Once sent, the bar is two actions instead of one. */
    submitted?: boolean;
    onUpdate?: () => void;
    onWithdraw?: () => void;
    /** How many are on the slate. Nothing chosen and the bar says so by saying nothing. */
    count?: number;
    /** Already formatted — `₣22`. */
    tax?: string;
    /** How many of his picks the slate spends. */
    picks?: number;
  },
) {
  /*
   * Same story as the draft bar: the page reserves what this measures rather than what CSS was
   * told it would be. Declared before the early return so the hook order is stable across both
   * shapes of the bar — two states, one component, one hook.
   */
  const barRef = useRef<HTMLDivElement>(null);
  useDockedHeight(barRef);

  if (submitted) {
    return dock(
      <div className="kdcommit" ref={barRef}>
        {/*
          "Unlock Keepers" / "Lock Keepers" (Tim, 26 August), replacing "Update slate" / "Submit".
          The old pair described what the APP does with the record; the new one describes what the
          manager is doing to his team, which is the thing he is actually deciding. It also makes
          the two states each other's opposite, which "Update" and "Submit" never were.
        */}
        <button type="button" className="kdsubmit" style={{ flex: 1 }} onClick={onUpdate}>
          Unlock keepers
        </button>
        <button type="button" className="kdwithdraw" onClick={onWithdraw}>Withdraw</button>
      </div>,
    );
  }
  /*
    The left of the bar changes job the moment something is ticked. With an empty slate the only
    thing worth saying is that there is nothing to lose; with three names on it the manager wants
    the running consequence — what he is keeping, what it costs, and how much of his draft it
    spends — which is exactly what the sheet will ask him to confirm.
  */
  const running = count !== undefined && count > 0;
  return dock(
    <div className="kdcommit" ref={barRef}>
      <span className="kdsaved">
        {saved === 'saving' ? 'Saving…'
          : saved === 'offline' ? 'Saved on this device — it will sync'
            : running
              ? (
                <>
                  <b>{count} keeper{count === 1 ? '' : 's'}</b>
                  <small>
                    {tax} tax · {picks} pick{picks === 1 ? '' : 's'} spent
                  </small>
                </>
              )
              : 'Saved automatically'}
      </span>
      <button type="button" className="kdsubmit" disabled={disabled} onClick={onSubmit}>
        Lock keepers
      </button>
    </div>
  );
}

export function SubmitSheet(
  { lines, tax, taxed, faabAfter, until, busy, onConfirm, onCancel }:
  {
    lines: readonly Line[];
    /** Already formatted — `₣22`. */
    tax: string;
    /** Whether any tax is owed. Amber is a warning and ₣0 is not one. */
    taxed: boolean;
    /** What he is left to bid with once the tax comes out. The number managers actually ask for. */
    faabAfter: string;
    /** When it can still be changed until — the reassurance that makes Submit an easy press. */
    until?: string | null;
    busy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
  },
) {
  useScrollLock(true);

  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onCancel]);

  /*
   * DOCKED, like the bar that opens it, and for a reason that took a submitted slate to find.
   * This sheet is `position: fixed` and was rendered where it sits in the tree — inside `main`,
   * which carries pull-to-refresh's transform — so it was drawn at the bottom of a page-height
   * box rather than the bottom of the screen: 2,626px down an 844px phone, with its "Submit
   * slate" button at 2,887. Pressing Submit looked like it did nothing, and the confirmation for
   * an irreversible league action sat where nobody could see what they were pressing.
   */
  return dock(
    <>
      <button type="button" className="sheetscrim kdscrim" aria-label="Keep editing" onPointerUp={onCancel} />
      <div className="moresheet kdsheet" role="dialog" aria-modal="true" aria-label="Submit your slate">
        <span className="sheetgrip" aria-hidden="true" />
        {/*
          THE SAME ROW THE BOARD DRAWS, at rest.
          The sheet used to set the name in one weight and the club and position as two grey
          words beside it, which made the last thing a manager sees before committing look like
          a different app from the list he built it in. It is the row's own shape now — surname
          first, the club and position chips under it, the pick on the right — with nothing
          tappable, because nothing here can be changed.
        */}
        <h2 className="kdsheeth">
          Lock {WORD[lines.length] ?? lines.length} keeper{lines.length === 1 ? '' : 's'}?
        </h2>
        {until && <p className="kdsheetsub">Changeable until {until}.</p>}

        <ul className="kdlines">
          {lines.map((l) => {
            const parts = l.player.trim().split(/\s+/);
            const given = parts.length > 1 ? parts[0]! : '';
            const surname = parts.length > 1 ? parts.slice(1).join(' ') : (parts[0] ?? '');
            return (
              <li key={l.player}>
                <span className="kdlwho">
                  <span className="kdlname">
                    <b>{surname}{given ? ',' : ''}</b>
                    {given && <span className="kdlgiven">{given}</span>}
                  </span>
                  <span className="kdlchips">
                    <ClubChip team={l.club} />
                    <PositionChip pos={l.pos} />
                    {l.tagged && <span className="kdltag">Franchised</span>}
                  </span>
                </span>
                <span className="kdlpick">
                  {l.code}
                  {l.overall !== null && <i>#{l.overall}</i>}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="kdsum">
          {/* Amber the moment it stops being zero, and not before — see the `taxed` prop. */}
          <div className={`kdsumrow tax${taxed ? ' owed' : ''}`}>
            <span className="lab">Luxury tax</span>
            <span className="val">{tax}</span>
          </div>
          {/*
            The number a manager actually asks for, and the one this screen never showed until now.
            The tax is paid in FAAB, so an eighth keeper is not an abstract ₣88 — it is ₣88 he will
            not have at the farm auction on the same night as the draft.
          */}
          <div className="kdsumrow after">
            <span className="lab">FAAB after tax</span>
            <span className="val">{faabAfter}</span>
          </div>
        </div>

        <div className="kdsheetacts">
          <button type="button" className="kdsubmit" disabled={busy} onClick={onConfirm}>
            {busy ? 'Locking…' : 'Lock keepers'}
          </button>
          <button type="button" className="kdkeep" onClick={onCancel}>Keep editing</button>
        </div>
      </div>
    </>,
  );
}
